<script setup lang="ts">
import { ref } from 'vue';

import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';
import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { usePreviouslyUserSessionStore } from '@apps/main/new-desktop/core/stores/previouslyUserSession/previouslyUserSessionStore';
import { getBoardBasisText } from '@apps/main/new-desktop/core/helpers/translation.helper';
import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { BoardBasisTag } from '@apps/main/new-desktop/modules/boardBasisTag';

import { AppLinkButton, AppRouterLink, AppStars } from '@libs/ui';

import type {
  HotelSelectionCardContentData,
  HotelSelectionCardContext,
  HotelSelectionCardSummaryData,
} from '../models';

const { t } = i18n.global;

interface HotelSelectionCardContentProps {
  data: HotelSelectionCardContentData;
  summaryData: HotelSelectionCardSummaryData;
  context: HotelSelectionCardContext;
  isSingleHotelRecipe: boolean;
}

const props = defineProps<HotelSelectionCardContentProps>();

interface HotelSelectionCardContentEmits {
  (eventName: 'peek-price'): void;
}

const emit = defineEmits<HotelSelectionCardContentEmits>();

const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();
const previouslyUserSessionStore = usePreviouslyUserSessionStore();

const hotelPriceCalendarText = ref<string>(
  props.isSingleHotelRecipe
    ? t('planner.low_price_calendar_button')
    : t('planner.total_price_and_dates')
);

function onNavigationTriggered(): void {
  const isPreviouslySeenHotel =
    previouslyUserSessionStore.previouslySeenHotelCodes.includes(
      props.context.hotelCode
    );
  statsigEventsStore.logHotelSelectionListCardClicked({
    isPreviouslySeenHotel,
  });
}
</script>

<template>
  <div class="hotel-selection-card-content">
    <div class="hotel-details">
      <div class="hotel-base-info">
        <AppRouterLink
          :link="
            routerStore.getHotelDetailsUrlInHotelSelection(
              { ingredientIndex: context.ingredientIndex },
              context.hotelCode
            )
          "
          font="font-body-m-bold"
          color="color-neutral-70"
          class="name"
          @navigation-triggered="onNavigationTriggered"
        >
          {{ props.data.title }}
        </AppRouterLink>
        <div class="location">{{ props.data.subtitle }}</div>
        <div class="ratings">
          <AppStars class="stars" :amount="props.data.stars" size="18" />
          <CustomersRatingTag
            v-if="props.data.guestRating"
            :guest-rating="props.data.guestRating"
          />
        </div>
      </div>

      <HotelAmenities class="amenities" :amenities="props.data.amenities" />

      <BoardBasisTag
        v-if="props.data.boardBasis && props.data.boardBasis?.[0]"
        :board-basis-code="props.data.boardBasis[0]"
        :translations-callback="getBoardBasisText"
      />

      <div class="hotel-description">
        <ul class="nearby-places">
          <li v-for="content in props.data.content" :key="content" class="item">
            <span class="item-text">
              {{ content }}
            </span>
          </li>
        </ul>
      </div>

      <div class="hotel-price-calendar">
        <AppLinkButton
          :text="hotelPriceCalendarText"
          left-icon="lowPriceCalendar"
          icon-size="18"
          size="s"
          :bold="true"
          @click="emit('peek-price')"
        />
      </div>
    </div>

    <div class="hotel-summary">
      <div class="hotel-deal-description">
        <DealCodeTag
          v-if="props.summaryData.grossPrice"
          :wrap="true"
          type="tag"
          class="campaign-code-tag"
        />
      </div>
      <div class="hotel-price-description">
        <div class="hotel-price">
          <div v-if="props.summaryData.grossPrice" class="gross-price">
            {{ props.summaryData.grossPrice }}
          </div>
          <div
            class="total-price"
            :class="{
              'with-discount': props.summaryData.grossPrice,
            }"
          >
            {{ props.summaryData.totalPrice }}
          </div>
        </div>
        <div class="label">
          {{ $t('planner.price_for_this_accommodation') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .hotel-details {
    display: flex;
    gap: var(--spacing-8);
    flex-direction: column;

    .hotel-base-info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .name {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .location {
        @include font-body-s-light;
        text-transform: capitalize;
        color: var(--color-neutral-70);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .ratings {
        display: flex;
        gap: var(--spacing-8);
        padding-top: var(--spacing-4);
        align-items: center;
      }
    }

    .hotel-description {
      padding-top: var(--spacing-4);
      padding-bottom: var(--spacing-4);
      ul {
        margin: 0;
        padding-left: var(--spacing-16);
        list-style: none;

        .item {
          @include font-label-s-light;
          color: var(--color-neutral-70);
          position: relative;
          // aliign to start of text above (18x wide icon + 4px gap)
          padding-left: 6px;

          // NOTE: Standard list-style is broken on mobile,
          // when using overflow + ellipsis on list element.
          // Hence custom dot element
          &::before {
            content: '';
            width: 3px;
            height: 3px;
            border-radius: 50%;
            position: absolute;
            background: var(--color-neutral-70);
            left: -9px;
            top: 50%;
            transform: translateY(-50%);
          }

          .item-text {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            line-clamp: 1;
            -webkit-box-orient: vertical;
            word-break: break-all;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &:not(:last-child) {
            margin-bottom: var(--spacing-4);
          }
        }
      }
    }

    .hotel-price-calendar {
      padding-bottom: var(--spacing-12);
    }
  }

  .hotel-summary {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-8);
    border-top: 1px dashed var(--color-neutral-40);
    padding-top: var(--spacing-12);

    .hotel-deal-description {
      .campaign-code-tag {
        justify-content: space-between;
        float: left;
      }
    }

    .hotel-price-description {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
      align-items: flex-end;
      .hotel-price {
        display: flex;
        align-items: center;
        gap: var(--spacing-4);
        .gross-price {
          @include font-body-m-bold;
          color: var(--color-neutral-70);
          text-decoration: line-through;
        }

        .total-price {
          @include font-heading-6;
          color: var(--color-neutral-70);

          &.with-discount {
            color: var(--color-error-dark);
          }
        }
      }
      .label {
        padding-bottom: var(--spacing-4);
        @include font-label-s-light;
        color: var(--color-neutral-70);
      }
    }
  }
}
</style>
